// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseDatePicker, BaseLabel, BaseSelect, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { IQueryParamListExport, ITransactionSummaryReport } from '~/utils/interface/reports';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_VALUE,
  optionViewReport,
} from '~/utils/constants/common';
import { IBaseOption, IHeaderCellType, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { formatCurrencyUSD, formatDate, getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { DateFormatEnum, ReportTypeEnum, RoleEnum } from '~/utils/enum/common';
import { getListUser } from '~/thunks/user/userThunk';
import { IQueryParamUser, IUser } from '~/utils/interface/user';
import { exportDataReport, getListTransactionSummaryReport } from '~/thunks/reports/reportsThunk';
// Styles, images, icons
import styles from './ReportsTransactionSummaryOrder.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction, view?: string): IHeaderCellType<ITransactionSummaryReport>[] => {
  return [
    {
      id: 'date',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>
            {t(
              (view === 'year' && 'reports_table_year') ||
                (view === 'month' && 'reports_table_month') ||
                'reports_table_date'
            )}
          </Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        const date = record?.date;
        const month = +record?.month;
        const year = +record?.year;

        if (date) {
          return formatDate(date, DateFormatEnum.MMM_D_YYYY);
        }

        if (month && year) {
          const fakeDate = new Date(year, month - 1, 1);
          return formatDate(fakeDate, DateFormatEnum.MMM_D_YYYY);
        }

        if (year) {
          return year.toString();
        }
      },
    },
    {
      id: 'quoteTotal',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_quotes')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return <Typography className={cx('textQty')}>{record?.quoteTotal || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'convertedToOrderTotal',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_converted_to_order')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return <Typography className={cx('textSubrental')}>{record?.convertedToOrderTotal || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'quoteCancelledTotal',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_cancelled')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return <Typography className={cx('textCancelled')}>{record?.quoteCancelledTotal || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'quoteExpiredTotal',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_expired_quotes')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return <Typography className={cx('textExpired')}>{record?.quoteExpiredTotal || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'quoteExpiredPercentage',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_expired_quotes_percentage')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return (
          <Typography className={cx('textExpired')}>
            {record?.quoteExpiredPercentage ? `${record?.quoteExpiredPercentage}%` : EMPTY_VALUE}
          </Typography>
        );
      },
    },
    {
      id: 'potentialIncome',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_potential')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return (
          <Typography className={cx('textQty')}>
            {formatCurrencyUSD({ value: record?.potentialIncome, showDecimal: true })}
          </Typography>
        );
      },
    },
    {
      id: 'actualIncome',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_actual')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return (
          <Typography className={cx('textSubrental')}>
            {formatCurrencyUSD({ value: record?.actualIncome, showDecimal: true })}
          </Typography>
        );
      },
    },
    {
      id: 'convertedToOrderPercentage',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_quotes_to_orders')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return (
          <Typography className={cx('textOrder')}>
            {record?.convertedToOrderPercentage ? `${record?.convertedToOrderPercentage}%` : EMPTY_VALUE}
          </Typography>
        );
      },
    },
    {
      id: 'quoteCancelledPercentage',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_orders_to_cancelled')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return (
          <Typography className={cx('textCancelled')}>
            {record?.quoteCancelledPercentage ? `${record?.quoteCancelledPercentage}%` : EMPTY_VALUE}
          </Typography>
        );
      },
    },
    {
      id: 'potentialToActualPercentage',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_potential_to_actual')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return (
          <Typography className={cx('textOrder')}>
            {record?.potentialToActualPercentage ? `${record?.potentialToActualPercentage}%` : EMPTY_VALUE}
          </Typography>
        );
      },
    },
    {
      id: 'total',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_total')}</Box>
        </Stack>
      ),
      render: (record: ITransactionSummaryReport) => {
        return <Typography className={cx('textQty')}>{record?.quoteTotal || EMPTY_VALUE}</Typography>;
      },
    },
  ];
};
const ReportsTransactionSummaryOrder = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [transactionSummaryReport, setTransactionSummaryReport] = useState<ITransactionSummaryReport[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    view: 'month',
    startDate: moment(new Date()).format(DateFormatEnum.YYYY_MM_DD),
    endDate: moment(new Date()).format(DateFormatEnum.YYYY_MM_DD),
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo

  const chartData = useMemo(() => {
    if (!transactionSummaryReport) return [];

    if (paramsObject.view === 'day') {
      return transactionSummaryReport.map((d) => ({
        x: d.date ? moment(d.date).format(DateFormatEnum.MMM_D_YYYY) : '',
        quoteTotal: Number(d.quoteTotal),
        convertedToOrderTotal: Number(d.convertedToOrderTotal),
        quoteCancelledTotal: Number(d.quoteCancelledTotal),
        quoteExpiredTotal: Number(d.quoteExpiredTotal),
      }));
    }

    if (paramsObject.view === 'month') {
      return transactionSummaryReport.map((d) => {
        const year = Number(d.year);
        const month = Number(d.month);
        return {
          x: moment(new Date(year, month - 1, 1)).format(DateFormatEnum.MMM_D_YYYY) || '',
          quoteTotal: Number(d.quoteTotal),
          convertedToOrderTotal: Number(d.convertedToOrderTotal),
          quoteCancelledTotal: Number(d.quoteCancelledTotal),
          quoteExpiredTotal: Number(d.quoteExpiredTotal),
        };
      });
    }

    return transactionSummaryReport.map((d) => ({
      x: d.year?.toString(),
      quoteTotal: Number(d.quoteTotal),
      convertedToOrderTotal: Number(d.convertedToOrderTotal),
      quoteCancelledTotal: Number(d.quoteCancelledTotal),
      quoteExpiredTotal: Number(d.quoteExpiredTotal),
    }));
  }, [transactionSummaryReport]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetTransactionSummaryReport();
  }, [paramsObject.page, paramsObject.view]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleGetTransactionSummaryReport = () => {
    setIsLoading(true);

    dispatch(getListTransactionSummaryReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { data } = res;

        setTransactionSummaryReport(data);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetTransactionSummaryReport();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.TRANSACTION_SUMMARY,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };
  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const fetchOptionsUser = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;
    const payloadParams: IQueryParamUser = {
      page: page,
      limit: DEFAULT_LIMIT_PAGE,
      role: RoleEnum.SALES_PERSON,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses: listCategory, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = handleConvertOptionUser(listCategory);

      const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

      return { optionsResp: formatOptions, hasMoreResp: hasMore };
    } catch (error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionsView = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    try {
      return { optionsResp: optionViewReport, hasMoreResp: false };
    } catch (error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const handleConvertOptionUser = (data: IUser[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: getFullName({ firstName: item.firstName, lastName: item.lastName }),
    }));

    return options;
  };

  //#endregion Handle Function

  return (
    <Stack className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_transaction_summary_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_transaction_summary_desc')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
            <BaseDatePicker
              mode='date'
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
            <BaseDatePicker
              mode='date'
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
            <BaseSelect
              isSearch
              gap={8}
              type='loadMore'
              styleType='contained'
              label={t('reports_financial_order_user_label')}
              value={paramsObject.accountId}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  // accountId: data?.value,
                }));
              }}
              callApiFn={fetchOptionsUser}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
            <BaseSelect
              gap={8}
              styleType='contained'
              label={t('reports_financial_order_group_by')}
              value={paramsObject.view}
              onChange={(data) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  view: data?.value,
                }));
              }}
              callApiFn={fetchOptionsView}
            />
          </Grid2>
        </Grid2>

        <Stack direction={'row'} justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            disabled={!paramsObject.startDate && !paramsObject.endDate && !paramsObject.accountId && !paramsObject.view}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            disabled={!transactionSummaryReport.length}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('viewChart')}>
        <ResponsiveContainer width='100%' height={320}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray='3 3' />
            <XAxis dataKey='x' />
            <YAxis />
            <Tooltip />

            <Line
              type='monotone'
              dataKey='quoteTotal'
              name={t('reports_transaction_summary_quote_sent')}
              stroke='#3b82f6'
              strokeWidth={2}
              dot={false}
            />
            <Line
              type='monotone'
              dataKey='convertedToOrderTotal'
              name={t('reports_transaction_summary_converted_to_order')}
              stroke='#22c55e'
              strokeWidth={2}
              dot={false}
            />
            <Line
              type='monotone'
              dataKey='quoteCancelledTotal'
              name={t('reports_transaction_summary_cancelled_orders')}
              stroke='#f97316'
              strokeWidth={2}
              dot={false}
            />
            <Line
              type='monotone'
              dataKey='quoteExpiredTotal'
              name={t('reports_transaction_summary_expired_quotes')}
              stroke='#ec4899'
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>

        <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={4}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Box bgcolor={'#2D80FC'} width={12} height={12} borderRadius={12} />

            <Typography className={cx('lineTitle')}>{t('reports_transaction_summary_quote_sent')}</Typography>
          </Stack>

          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Box bgcolor={'#77B900'} width={12} height={12} borderRadius={12} />

            <Typography className={cx('lineTitle')}>{t('reports_transaction_summary_converted_to_order')}</Typography>
          </Stack>

          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Box bgcolor={'#F86B38'} width={12} height={12} borderRadius={12} />

            <Typography className={cx('lineTitle')}>{t('reports_transaction_summary_cancelled_orders')}</Typography>
          </Stack>

          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Box bgcolor={'#EC4899'} width={12} height={12} borderRadius={12} />

            <Typography className={cx('lineTitle')}>{t('reports_transaction_summary_expired_quotes')}</Typography>
          </Stack>
        </Stack>
      </Box>

      <Box className={cx('tableContainer')}>
        <BaseTable dataSource={transactionSummaryReport} columns={columns(t, paramsObject?.view)} />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ReportsTransactionSummaryOrder;
